// client/src/coms/views/canvas_parts/CanvasRightSidebarExtensions.js
import React from 'react';
import CanvasDistortionSettingsPanel from './CanvasDistortionSettingsPanel';
import CanvasPencilSettingsPanel from './CanvasPencilSettingsPanel';
import CanvasReplaySettingsPanel from './CanvasReplaySettingsPanel';
import CanvasBrushPresetPanel from './CanvasBrushPresetPanel';
import CanvasPaintSettingsPanel from './CanvasPaintSettingsPanel';
import CanvasFillSettingsPanel from './CanvasFillSettingsPanel';
import CanvasShapeSettingsPanel from './CanvasShapeSettingsPanel';
import CanvasSelectionSettingsPanel from './CanvasSelectionSettingsPanel';
import CanvasToolControlsPanel from './CanvasToolControlsPanel';
import CanvasTextSettingsPanel from './CanvasTextSettingsPanel';

export default function CanvasRightSidebarExtensions({
  activeTool,
  liquifyMode,
  setLiquifyMode,
  liquifySettings,
  setLiquifySettings,
  activeBrushPreset,
  setActiveTool,
  setActiveBrushPreset,
  importedBrushPresets,
  activeImportedBrushId,
  setActiveImportedBrushId,
  brushSize,
  setBrushSize,
  brushSoftness,
  setBrushSoftness,
  activeToolOpacity,
  onToolOpacityChange,
  onAddImportedBrushPresets,
  pencilOrientation,
  setPencilOrientation,
  pencilGrade,
  setPencilGrade,
  replayPanelOpen,
  replayEventCount,
  replayCursor,
  replayPlaying,
  replaySpeed,
  onReplayPlayPause,
  onReplayStop,
  onReplayRewind,
  onReplayStepBack,
  onReplayStepForward,
  onReplaySpeedChange,
  onReplaySeek,
  onReplayClose,
  gamePanel,
  strokeColor = '#d52b24',
  setStrokeColor,
  paintSettings,
  onPaintSettingsChange,
  paintDebugViewMode = 'final',
  setPaintDebugViewMode,
  onDryPaint,
  onClearPaint,
  bucketExpansion = 0,
  setBucketExpansion,
  jitterAmount,
  setJitterAmount,
  calligraphySettings,
  setCalligraphySettings,
  airbrushDistance,
  setAirbrushDistance,
  airbrushPaintFlow,
  setAirbrushPaintFlow,
  airbrushPressurePaintFlowEnabled,
  setAirbrushPressurePaintFlowEnabled,
  airbrushAirPressure,
  setAirbrushAirPressure,
  textSettings,
  setTextSettings,
  transformMode,
  setTransformMode,
  perspectiveAxis = 'vertical',
  setPerspectiveAxis,
  onTransformCommand,
  modelViewOpen = false,
  paintMaterialSettings,
  onPaintMaterialSettingsChange,
  pbrBrushActive = false,
  lock3dPaintToStartingSurface = false,
  onLock3dPaintToStartingSurfaceChange,
}) {
  const panels = [];

  if (React.isValidElement(gamePanel)) {
    panels.push({
      id: 'game',
      node: gamePanel,
    });
  }

  if (replayPanelOpen) {
    panels.push({
      id: 'replay',
      node: (
        <CanvasReplaySettingsPanel
          eventCount={replayEventCount}
          replayCursor={replayCursor}
          replayPlaying={replayPlaying}
          replaySpeed={replaySpeed}
          onReplayPlayPause={onReplayPlayPause}
          onReplayStop={onReplayStop}
          onReplayRewind={onReplayRewind}
          onReplayStepBack={onReplayStepBack}
          onReplayStepForward={onReplayStepForward}
          onReplaySpeedChange={onReplaySpeedChange}
          onReplaySeek={onReplaySeek}
          onReplayClose={onReplayClose}
        />
      ),
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
        {panels.map((panel) => (
          <React.Fragment key={panel.id}>{panel.node}</React.Fragment>
        ))}
      </div>
    );
  }

  const toolControlsPanel = (
    <CanvasToolControlsPanel
      activeTool={activeTool}
      activeBrushPreset={activeBrushPreset}
      brushSize={brushSize}
      setBrushSize={setBrushSize}
      brushSoftness={brushSoftness}
      setBrushSoftness={setBrushSoftness}
      activeToolOpacity={activeToolOpacity}
      onToolOpacityChange={onToolOpacityChange}
      jitterAmount={jitterAmount}
      setJitterAmount={setJitterAmount}
      calligraphySettings={calligraphySettings}
      setCalligraphySettings={setCalligraphySettings}
      airbrushDistance={airbrushDistance}
      setAirbrushDistance={setAirbrushDistance}
      airbrushPaintFlow={airbrushPaintFlow}
      setAirbrushPaintFlow={setAirbrushPaintFlow}
      airbrushPressurePaintFlowEnabled={airbrushPressurePaintFlowEnabled}
      setAirbrushPressurePaintFlowEnabled={setAirbrushPressurePaintFlowEnabled}
      airbrushAirPressure={airbrushAirPressure}
      setAirbrushAirPressure={setAirbrushAirPressure}
      transformMode={transformMode}
      setTransformMode={setTransformMode}
      perspectiveAxis={perspectiveAxis}
      setPerspectiveAxis={setPerspectiveAxis}
      onTransformCommand={onTransformCommand}
      modelViewOpen={modelViewOpen}
      paintMaterialSettings={paintMaterialSettings}
      onPaintMaterialSettingsChange={onPaintMaterialSettingsChange}
      pbrBrushActive={pbrBrushActive}
      lock3dPaintToStartingSurface={lock3dPaintToStartingSurface}
      onLock3dPaintToStartingSurfaceChange={onLock3dPaintToStartingSurfaceChange}
    />
  );

  if (React.isValidElement(toolControlsPanel) && toolControlsPanel.type) {
    panels.push({ id: 'tool-controls', node: toolControlsPanel });
  }

  const showPaintBrushPresets = activeTool === 'paint' && String(paintSettings?.tool || 'tube') === 'brush';
  const showBrushPresetPanel = (activeTool === 'pen' && activeBrushPreset === 'pen') || activeTool === 'eraser' || showPaintBrushPresets;

  const brushPresetPanel = showBrushPresetPanel ? (
    <CanvasBrushPresetPanel
      activeTool={activeTool}
      activeBrushPreset={activeBrushPreset}
      setActiveTool={setActiveTool}
      setActiveBrushPreset={setActiveBrushPreset}
      importedBrushPresets={importedBrushPresets}
      activeImportedBrushId={activeImportedBrushId}
      setActiveImportedBrushId={setActiveImportedBrushId}
      onAddImportedBrushPresets={onAddImportedBrushPresets}
      paintMode={showPaintBrushPresets}
    />
  ) : null;

  if (brushPresetPanel && !showPaintBrushPresets) {
    panels.push({
      id: 'brushes',
      node: brushPresetPanel,
    });
  }

  if (activeTool === 'pen' && activeBrushPreset === 'pencil') {
    panels.push({
      id: 'pencil',
      node: (
        <CanvasPencilSettingsPanel
          pencilOrientation={pencilOrientation}
          setPencilOrientation={setPencilOrientation}
          pencilGrade={pencilGrade}
          setPencilGrade={setPencilGrade}
        />
      ),
    });
  }





  if (activeTool === 'line' || activeTool === 'rectangle' || activeTool === 'ellipse') {
    panels.push({
      id: 'shape-settings',
      node: (
        <CanvasShapeSettingsPanel
          activeTool={activeTool}
          setActiveTool={setActiveTool}
        />
      ),
    });
  }

  if (activeTool === 'marquee_rect' || activeTool === 'marquee_ellipse' || activeTool === 'marquee_polygon' || activeTool === 'marquee_polygon_edit') {
    panels.push({
      id: 'selection-settings',
      node: (
        <CanvasSelectionSettingsPanel
          activeTool={activeTool}
          setActiveTool={setActiveTool}
        />
      ),
    });
  }

  if (activeTool === 'text') {
    panels.push({
      id: 'text-settings',
      node: (
        <CanvasTextSettingsPanel
          textSettings={textSettings}
          setTextSettings={setTextSettings}
        />
      ),
    });
  }

  if (activeTool === 'bucket') {
    panels.push({
      id: 'fill-settings',
      node: (
        <CanvasFillSettingsPanel
          bucketExpansion={bucketExpansion}
          setBucketExpansion={setBucketExpansion}
        />
      ),
    });
  }

  if (activeTool === 'paint') {
    panels.push({
      id: 'paint-settings',
      node: (
        <CanvasPaintSettingsPanel
          strokeColor={strokeColor}
          setStrokeColor={setStrokeColor}
          brushSize={brushSize}
          setBrushSize={setBrushSize}
          paintSettings={paintSettings}
          onPaintSettingsChange={onPaintSettingsChange}
          paintDebugViewMode={paintDebugViewMode}
          setPaintDebugViewMode={setPaintDebugViewMode}
          onDryPaint={onDryPaint}
          onClearPaint={onClearPaint}
        />
      ),
    });

    if (brushPresetPanel) {
      panels.push({
        id: 'brushes',
        node: brushPresetPanel,
      });
    }
  }

  if (activeTool === 'liquify') {
    panels.push({
      id: 'distortion',
      node: (
        <CanvasDistortionSettingsPanel
          activeTool={activeTool}
          liquifyMode={liquifyMode}
          setLiquifyMode={setLiquifyMode}
          liquifySettings={liquifySettings}
          setLiquifySettings={setLiquifySettings}
          brushSize={brushSize}
          setBrushSize={setBrushSize}
        />
      ),
    });
  }

  if (!panels.length) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
      {panels.map((panel) => (
        <React.Fragment key={panel.id}>{panel.node}</React.Fragment>
      ))}
    </div>
  );
}
